import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { BlueprintGrid } from "./Blueprint";
import { EASE } from "./motion";
import { LocationStep, DetailsStep } from "./sitevisit/Steps";
import { PayStep, ReceiptStep } from "./sitevisit/PayAndReceipt";

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;
const STEPS = ["Location & fee", "Your details", "Pay via UPI", "Receipt"];
const INITIAL = { region: "kerala", location_id: "kozhikode", days: 0, nights: 0, urgent: false, repeat: false, visit_date: "", name: "", phone: "", email: "", site_address: "" };

const QuoteCard = ({ quote, loading }) => (
  <div className="bg-[#161614] text-[#FBFBF9] p-8 relative overflow-hidden" data-testid="visit-quote-card">
    <BlueprintGrid dark />
    <div className="relative">
      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C29B38]">Site visit fee</p>
      <AnimatePresence mode="wait">
        <motion.p key={quote?.pay_now ?? "x"} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35, ease: EASE }} className={`mt-5 font-serif font-light text-4xl sm:text-5xl leading-none ${loading ? "opacity-60" : ""}`} data-testid="visit-pay-now">
          {quote ? inr(quote.pay_now) : "—"}
        </motion.p>
      </AnimatePresence>
      <p className="mt-2 font-mono text-[11px] tracking-[0.15em] text-stone-400" data-testid="visit-pay-note">{quote?.balance ? `50% advance now · ${inr(quote.balance)} balance before leaving site` : "Payable now · full fee"}</p>
      {quote && (
        <dl className="mt-6 border-t border-stone-700 divide-y divide-stone-700 text-xs" data-testid="visit-quote-lines">
          {quote.lines.map((l) => (
            <div key={l.label} className="flex justify-between gap-4 py-2.5"><dt className="text-stone-400 font-light">{l.label}</dt><dd className="font-mono text-stone-200 whitespace-nowrap">{inr(l.amount)}</dd></div>
          ))}
          <div className="flex justify-between gap-4 py-2.5 font-mono"><dt className="text-stone-300 uppercase tracking-wider">Total</dt><dd data-testid="visit-total">{inr(quote.total)}</dd></div>
        </dl>
      )}
      <p className="mt-5 text-[11px] leading-relaxed text-stone-500">{quote?.note} Train/flight for out-of-state visits and site extras are not included.</p>
    </div>
  </div>
);

export const SiteVisit = () => {
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState(INITIAL);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/site-visit/rates`).then((r) => r.json()).then(setRates).catch(() => toast.error("Could not load the rate card"));
  }, []);

  useEffect(() => {
    if (!rates) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/site-visit/quote`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ region: form.region, location_id: form.location_id, days: form.days, nights: form.nights, urgent: form.urgent, repeat: form.repeat }) });
        if (res.ok) setQuote(await res.json());
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [rates, form.region, form.location_id, form.days, form.nights, form.urgent, form.repeat]);

  const next = async () => {
    if (step === 0) {
      if (!form.visit_date) return toast.error("Pick a preferred visit date");
      return setStep(1);
    }
    if (step === 1) {
      if (!form.name || !form.phone || !form.email) return toast.error("Name, phone and email are needed for the receipt");
      setBusy(true);
      try {
        const res = await fetch(`${API}/api/site-visit/bookings`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
        const data = await res.json();
        if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Please check the details and try again");
        setBooking(data);
        setStep(2);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setBusy(false);
      }
    }
  };
  const reset = () => { setForm(INITIAL); setBooking(null); setStep(0); };

  return (
    <section id="site-visit" className="relative bg-[#F4F3EE] py-24 lg:py-36 overflow-hidden" data-testid="site-visit-section">
      <BlueprintGrid className="opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow className="mb-6">Book a site visit</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900 leading-tight">Get Gokul to your plot.</h2>
          <p className="mt-5 text-stone-600 font-light leading-relaxed">Choose where your site is, see the visit fee instantly, pay by UPI and download your receipt. We drive from Thalassery for Kerala sites and travel by train or flight for the rest of India.</p>
        </Reveal>

        <ol className="mt-12 flex flex-wrap gap-x-8 gap-y-3" data-testid="visit-stepper">
          {STEPS.map((s, i) => (
            <li key={s} className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] ${i === step ? "text-stone-900" : i < step ? "text-[#2D6A4F]" : "text-stone-400"}`} aria-current={i === step ? "step" : undefined} data-testid={`visit-step-${i}`}>
              <span className={`w-6 h-6 flex items-center justify-center border text-[10px] ${i === step ? "border-stone-900 bg-stone-900 text-[#FBFBF9]" : i < step ? "border-[#2D6A4F]" : "border-stone-300"}`}>{i + 1}</span>{s}
            </li>
          ))}
        </ol>

        <div className="mt-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            {!rates ? (
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-stone-500" data-testid="visit-loading">Loading rate card…</p>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.4, ease: EASE }}>
                  {step === 0 && <LocationStep rates={rates} form={form} setForm={setForm} />}
                  {step === 1 && <DetailsStep form={form} setForm={setForm} />}
                  {step === 2 && booking && <PayStep booking={booking} api={API} onPaid={(b) => { setBooking(b); setStep(3); toast.success("Receipt issued"); }} />}
                  {step === 3 && booking && <ReceiptStep booking={booking} onReset={reset} />}
                </motion.div>
              </AnimatePresence>
            )}
            {rates && step < 2 && (
              <div className="mt-10 flex items-center gap-4">
                {step > 0 && <button type="button" onClick={() => setStep(step - 1)} className="btn-ghost" data-testid="visit-back-btn"><ArrowLeft size={16} /> Back</button>}
                <motion.button whileTap={{ scale: 0.97 }} type="button" onClick={next} disabled={busy || !quote} className="btn-dark disabled:opacity-60" data-testid="visit-next-btn">
                  {busy ? <span className="blocks"><i /><i /><i /></span> : step === 0 ? <>Continue <ArrowRight size={16} /></> : <>Proceed to pay {quote ? inr(quote.pay_now) : ""} <ArrowRight size={16} /></>}
                </motion.button>
              </div>
            )}
          </div>
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            {step < 2 ? <QuoteCard quote={quote} loading={loading} /> : step === 2 && booking && (
              <div className="border border-stone-300 p-6 font-mono text-xs divide-y divide-stone-200 bg-white" data-testid="visit-booking-summary">
                {[["Booking", booking.booking_id], ["Location", booking.place], ["Date", booking.visit_date], ["Client", booking.name], ["Total", inr(booking.total)], ["Pay now", inr(booking.pay_now)]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-3"><dt className="text-stone-500 uppercase tracking-wider">{k}</dt><dd className="text-right text-stone-900">{v}</dd></div>
                ))}
              </div>
            )}
            {rates && (
              <details className="mt-6 group" data-testid="visit-terms">
                <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900 list-none flex items-center gap-2">Rate card & terms <span className="transition-transform group-open:rotate-90">›</span></summary>
                <ul className="mt-4 space-y-2 text-xs text-stone-600 font-light leading-relaxed">
                  {rates.terms.map((t) => <li key={t} className="flex gap-2"><span className="text-[#B85C38]">—</span>{t}</li>)}
                </ul>
              </details>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
